import { useMemo, useState } from 'react';

import { useAuth, useRecruiterId } from '../lib/use-auth.js';
import { useI18n } from '../lib/use-i18n.js';
import { usePlatformBrand } from '../lib/use-platform-brand.js';
import { useTrackedSnapshot } from '../lib/use-tracked-snapshot.js';
import { recruiterPayoutsRepoFor } from '../lib/repositories/recruiters-repo.js';
import type { AgentPayout } from '@kilat/shared';

import {
  PayoutsHero,
  PayoutsHeroSkeleton,
  PayoutsList,
  PayoutsListSkeleton,
  PayoutsPageShell,
  PayoutsStateCard,
} from '../features/payouts/payouts-view.js';

/**
 * /recruiter/payouts — payout history for the recruiter persona. Reads
 * `recruiters/{recruiterId}/payouts/{payoutId}`, written by the SAME
 * `executePayoutBatch` Cloud Function as agent payouts with
 * `kind: 'recruiter'` (the super-admin `/superadmin/payouts` "Pay recruiters"
 * tab triggers them). Mirror of the agent `/payouts` page with recruiter
 * gating + copy; the presentational surface is shared via `payouts-view`.
 *
 * Kept SEPARATE from the agent payout history — a user who is both an agent
 * and a recruiter has two distinct ledgers and two distinct payout streams.
 * The route is additionally gated by the ADR-0023 `RecruiterGate` in App.tsx.
 */
export function RecruiterPayoutsPage() {
  const { user, loading: authLoading } = useAuth();
  const recruiterId = useRecruiterId();
  const t = useI18n();
  const brand = usePlatformBrand();
  const [loadError, setLoadError] = useState<Error | null>(null);
  const [retryNonce, setRetryNonce] = useState(0);

  const subscribe = useMemo(() => {
    if (!recruiterId) return null;
    const repo = recruiterPayoutsRepoFor(recruiterId);
    return (cb: (vs: AgentPayout[]) => void) =>
      repo.listSnapshot(
        (v) => {
          setLoadError(null);
          cb(v);
        },
        (err) => setLoadError(err),
      );
  }, [recruiterId]);

  const { data: payouts, loading } = useTrackedSnapshot<AgentPayout[]>(
    ['recruiter-payouts', recruiterId, retryNonce],
    subscribe,
  );

  if (authLoading) {
    return (
      <PayoutsPageShell>
        <PayoutsHeroSkeleton />
      </PayoutsPageShell>
    );
  }

  if (!user) {
    return (
      <PayoutsPageShell>
        <PayoutsStateCard
          title={t.t('agent.payouts.signedOutTitle')}
          body={t.t('agent.payouts.recruiterSignedOutBody')}
          ctaLabel={t.t('agent.payouts.signInCta')}
          ctaHref="/recruiter"
          ctaNote={t.t('agent.payouts.signInNote')}
        />
      </PayoutsPageShell>
    );
  }

  if (!recruiterId) {
    return (
      <PayoutsPageShell>
        <PayoutsStateCard
          title={t.t('agent.payouts.notRecruiterTitle')}
          body={t.t('agent.payouts.notRecruiterBody')}
          ctaLabel={t.t('agent.payouts.notRecruiterCta')}
          ctaHref={`https://${brand.domains.marketing}/become-a-recruiter`}
        />
      </PayoutsPageShell>
    );
  }

  if (loadError) {
    return (
      <PayoutsPageShell>
        <PayoutsStateCard
          title={t.t('agent.payouts.loadErrorTitle')}
          body={t.t('agent.loadError.body')}
          onRetry={() => {
            setLoadError(null);
            setRetryNonce((n) => n + 1);
          }}
          retryLabel={t.t('agent.loadError.retry')}
        />
      </PayoutsPageShell>
    );
  }

  if (loading || payouts === null) {
    return (
      <PayoutsPageShell>
        <PayoutsHeroSkeleton />
        <PayoutsListSkeleton />
      </PayoutsPageShell>
    );
  }

  if (payouts.length === 0) {
    return (
      <PayoutsPageShell>
        <PayoutsHero payouts={[]} />
        <PayoutsStateCard
          title={t.t('agent.payouts.emptyTitle')}
          body={t.t('agent.payouts.recruiterEmptyBody')}
          ctaLabel={t.t('agent.payouts.recruiterEmptyCta')}
          ctaHref="/recruiter/earnings"
        />
      </PayoutsPageShell>
    );
  }

  return (
    <PayoutsPageShell>
      <PayoutsHero payouts={payouts} />
      <PayoutsList payouts={payouts} csvFilenamePrefix="kilat-recruiter-payouts" />
    </PayoutsPageShell>
  );
}
